import { Link, useNavigate } from 'react-router-dom';
import { Bike, ChefHat, Clock, Headphones, Leaf, MapPin, ShoppingBag, Store } from 'lucide-react';
import { useCart } from '../../lib/cart';
import { money } from '../../lib/format';
import { todayHours, useI18n } from '../../i18n';
import { categoryImage, usePublic } from './context';

export function LandingPage() {
  const { restaurant: r, menu } = usePublic();
  const { t, lang, demo } = useI18n();
  const cart = useCart();
  const nav = useNavigate();
  const base = `/r/${r.slug}`;
  const popular = menu.items.filter((i) => i.config.popular).slice(0, 4);
  const promoItem = menu.items.find((i) => i.name === 'Pepperoni') ?? menu.items[0];

  const choose = (mode: 'DELIVERY' | 'PICKUP') => {
    cart.setMode(mode);
    nav(`${base}/menu`);
  };

  const icons = [<ChefHat key="c" />, <Leaf key="l" />, <Bike key="b" />, <Headphones key="h" />];
  const features = t.landing.features.map((f, i) => ({ icon: icons[i], t: f.t, d: i === 3 ? r.phone : f.d }));

  return (
    <main className="pub-main">
      <section className="hero">
        <div>
          <div className="flag" aria-hidden>
            <i />
            <i />
            <i />
          </div>
          <h1>{r.name}</h1>
          <p className="tagline">{demo(r.description) || t.landing.taglineFallback}</p>
          <div className="row wrap" style={{ gap: 12 }}>
            <div className="mode-toggle" role="group" aria-label={t.landing.orderMode}>
              <button className={cart.mode === 'DELIVERY' ? 'active' : ''} onClick={() => choose('DELIVERY')} disabled={!r.deliveryEnabled} data-testid="hero-delivery">
                <Bike size={18} /> {t.common.delivery}
              </button>
              <button className={cart.mode === 'PICKUP' ? 'active' : ''} onClick={() => choose('PICKUP')} disabled={!r.pickupEnabled} data-testid="hero-pickup">
                <Store size={18} /> {t.common.pickup}
              </button>
            </div>
            <Link to={`${base}/menu`} className="btn btn-outline-light btn-lg">
              {t.common.viewMenu}
            </Link>
          </div>
        </div>
        <div className="hero-img">
          <img src={r.heroImageUrl || '/food/pizza-pepperoni.svg'} alt={t.landing.heroAlt} />
        </div>
      </section>

      <div className="info-bar">
        <div>
          <span className="ico">
            <MapPin size={20} />
          </span>
          <div className="grow">
            <b>{r.address.line1}</b>
            <div className="muted small">{r.address.city}</div>
          </div>
        </div>
        <div>
          <span className="ico" style={{ color: r.openStatus.open ? 'var(--green-dark)' : 'var(--muted)' }}>
            <Clock size={20} />
          </span>
          <div>
            <b style={{ color: r.openStatus.open ? 'var(--green-dark)' : 'var(--red-dark)' }} data-testid="open-status">
              {r.openStatus.open ? t.landing.openNow : t.common.closed}
            </b>
            <div className="muted small">
              {todayHours(r.openStatus, lang, t)}
              {!r.openStatus.open && r.acceptingOrders ? t.landing.ordersAccepted : ''}
            </div>
          </div>
        </div>
        <div>
          <span className="ico">
            <Bike size={20} />
          </span>
          <div>
            <b>{t.landing.deliveryEta(r.deliveryEstimate.min, r.deliveryEstimate.max)}</b>
            <div className="muted small">{t.landing.deliveryInfo(money(r.deliveryFeeCents), r.deliveryRadiusKm)}</div>
          </div>
        </div>
      </div>

      {r.promo.enabled && r.promo.title && (
        <section className="section promo">
          <div className="promo-text">
            <small>{demo(r.promo.subtitle)}</small>
            <h3>{demo(r.promo.title)}</h3>
            <Link to={`${base}/product/${promoItem.id}`} className="btn btn-dark btn-lg">
              {t.landing.orderNow}
            </Link>
          </div>
          <img src="/food/pizza-supreme.svg" alt="" />
        </section>
      )}

      <section className="section">
        <div className="section-head">
          <h2 className="section-title">{t.landing.ourCategories}</h2>
          <Link to={`${base}/menu`} className="btn btn-ghost">
            {t.landing.fullMenu}
          </Link>
        </div>
        <div className="cat-row">
          {menu.categories.map((c) => (
            <button key={c.id} className="cat-tile" onClick={() => nav(`${base}/menu?cat=${c.id}`)}>
              <span className="cimg">
                <img src={categoryImage(menu, c.id)} alt="" />
              </span>
              <span>{demo(c.name)}</span>
            </button>
          ))}
        </div>
      </section>

      {popular.length > 0 && (
        <section className="section">
          <div className="section-head">
            <h2 className="section-title">{t.landing.favourites}</h2>
          </div>
          <div className="product-grid">
            {popular.map((i) => (
              <Link key={i.id} to={`${base}/product/${i.id}`} className="product-card">
                <div className="pimg">
                  <img src={i.imageUrl ?? ''} alt={demo(i.name)} />
                </div>
                <div className="pbody">
                  <h3>{demo(i.name)}</h3>
                  <p className="desc">{demo(i.description)}</p>
                  <span className="price">{t.landing.from(money(i.startingPriceCents))}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="section features">
        {features.map((f) => (
          <div key={f.t} className="feature">
            <div className="ico">{f.icon}</div>
            <b>{f.t}</b>
            <p className="muted small">{f.d}</p>
          </div>
        ))}
      </section>

      {cart.count > 0 && (
        <div className="sticky-cta">
          <Link to={`${base}/cart`} className="btn btn-primary btn-lg btn-block">
            <ShoppingBag size={18} /> {t.landing.viewCart(money(cart.subtotalCents))}
          </Link>
        </div>
      )}
    </main>
  );
}
